import { useMemo } from 'react'
import { type WorkflowData, type WorkflowId } from '@terros/common'
import { useLocalStorage } from '@mantine/hooks'
import { useWorkflowFeature, useWorkflowForCanvassFeature, useWorkflows } from '@front/model'
import { useStorageKey } from './useStorageKey'

type WorkflowResult = {
  workflowId: WorkflowId | undefined
  setWorkflowId: (workflowId: WorkflowId) => void
  shouldUseWorkflow: boolean
  options: WorkflowData[]
  isLoading: boolean
  error: string | undefined
}
export const useSelectedWorkflow = (): WorkflowResult => {
  const { data: workflows = [], isLoading: workflowListLoading, error } = useWorkflows()
  const defaultWorkflowId = workflows.at(0)?.workflowId
  const storageKey = useStorageKey('selectedWorkflowId')
  const [_workflowId, setWorkflowId] = useLocalStorage({
    key: storageKey,
    defaultValue: defaultWorkflowId,
  })

  const { isEnabled: workflowsEnabled, isLoading: workflowsLoading } = useWorkflowFeature()
  const { isEnabled: workflowsForCanvassEnabled, isLoading: workflowsForCanvassLoading } =
    useWorkflowForCanvassFeature()
  const shouldUseWorkflow = workflowsEnabled && workflowsForCanvassEnabled
  const isLoadingWorkflowList = shouldUseWorkflow && workflowListLoading

  const workflowId = useMemo(() => {
    if (!shouldUseWorkflow) return undefined
    if (workflows.find((w) => w.workflowId === _workflowId)) return _workflowId
    return defaultWorkflowId
  }, [_workflowId, shouldUseWorkflow, defaultWorkflowId, workflows])

  return {
    workflowId,
    setWorkflowId,
    shouldUseWorkflow,
    options: workflows,
    isLoading: workflowsLoading || workflowsForCanvassLoading || isLoadingWorkflowList,
    error,
  }
}
